import React, { useState } from 'react';
import { connect } from 'react-redux';
import { isMultiServerSession } from '../util/sessionMode';
import { buildDrawerSections } from '../util/cameraDrawer';
import ActionsMenu from './ActionsMenu';
import { buildLayoutMenuItems } from './savedLayoutMenu';

/**
 * CameraDrawer — MCPlayback camera-picker bottom-sheet (Phase 1d).
 *
 * Slides up from the bottom (mobile-portrait mock `.drawer-*`). Slot-scoped: the header names the
 * target slot. Sections (matches the mock): Loaded cameras (✕ to unassign) · Other cameras on this
 * NVR (tap to load) · Saved layouts (Phase 2e — placeholder for now) · Other systems (eview only,
 * tap a system to expand its cameras). Client-side search narrows the loaded + this-NVR lists only
 * (inventory §10) and hides the Saved/Systems sections while active.
 *
 * "Reuses NvrCameraTree" = reuses its DATA source (state.server.cameras/allCameras/authServers +
 * isMultiServerSession + the eview server-grouping pattern), not its flat-list/accordion markup —
 * the sectioned bottom-sheet is a different presentation. Pure-component named export for testing;
 * the connected default supplies the camera state. The view owns the dispatch via onPick/onUnassign.
 */
export const CameraDrawer = ({
  open,
  onClose,
  targetSlot = 0,
  replacing = false,
  slots = [],
  cameras = [],
  allCameras = [],
  authServers = [],
  isEviewSession = false,
  currentSerial,
  savedLayouts = [],
  atLayoutCap = false,
  onPick,
  onUnassign,
  onLoadLayout,
  onSaveLayout,
  onDeleteLayout,
  onRenameLayout,
  onReplaceLayout,
}) => {
  const [filterText, setFilterText] = useState('');
  const [expandedSystems, setExpandedSystems] = useState({});
  // "+ Save current as layout" inline name entry (2e).
  const [savingName, setSavingName] = useState(null); // null = not saving; '' or text = entering a name
  // Inline-rename for a saved-layout row (v1.5; one at a time). Commit on Enter/blur, cancel on Esc.
  const [renamingLayoutId, setRenamingLayoutId] = useState(null);
  const commitLayoutRename = (id, value) => {
    const name = (value || '').trim();
    if (name && onRenameLayout) onRenameLayout(id, name);
    setRenamingLayoutId(null);
  };

  if (!open) return null;

  const ft = filterText.trim();
  const { loaded, available, otherSystems } = buildDrawerSections({
    slots, cameras, allCameras, authServers, isEviewSession, currentSerial, filterText,
  });
  const title = replacing ? `Replace slot ${targetSlot + 1}…` : `Pick a camera for slot ${targetSlot + 1}`;
  const toggleSystem = (serial) => setExpandedSystems((prev) => ({ ...prev, [serial]: !prev[serial] }));

  // One camera row. `slotIndex` non-null => loaded (SLOT badge + ✕ unassign); else pickable.
  const camRow = ({ key, rawCam, color, idText, name, slotIndex }) => (
    <div className="mcpb-drawer-cam-row" key={key}>
      <button type="button" className="mcpb-drawer-cam-row__body" onClick={() => onPick && onPick(rawCam)}>
        <span className="mcpb-drawer-cam-row__dot" style={{ background: color }} />
        <span className="mcpb-drawer-cam-row__id">{idText}</span>
        <span className="mcpb-drawer-cam-row__name">{name}</span>
        {slotIndex != null && <span className="mcpb-drawer-cam-row__slot">SLOT {slotIndex + 1}</span>}
      </button>
      {slotIndex != null && (
        <button
          type="button"
          className="mcpb-drawer-cam-row__x"
          aria-label={`Unassign ${name}`}
          onClick={() => onUnassign && onUnassign(slotIndex)}
        >
          &#10005;
        </button>
      )}
    </div>
  );

  const unassignedDot = 'var(--mcpb-color-text-muted)';

  return (
    <>
      <div className="mcpb-drawer-backdrop" onClick={onClose} />
      <div className="mcpb-drawer-panel" role="dialog" aria-label={title}>
        <div className="mcpb-drawer-header">
          <span className="mcpb-drawer-header__title">{title}</span>
          <button type="button" className="mcpb-drawer-header__close" aria-label="Close" onClick={onClose}>&times;</button>
        </div>

        <div className="mcpb-drawer-search-wrap">
          <input
            type="text"
            className="mcpb-drawer-search"
            aria-label="Search cameras"
            placeholder="Search by name or number"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <div className="mcpb-drawer-body">
          <div className="mcpb-drawer-section-label">Loaded cameras · tap ✕ to unassign</div>
          {/* .mcpb-drawer-loaded-list lets the loaded rows flow in columns in mobile-landscape (same
              multi-column treatment as the this-NVR list below). */}
          <div className="mcpb-drawer-loaded-list">
            {loaded.length === 0
              ? <div className="mcpb-drawer-empty">No loaded cameras{ft ? ` match "${ft}"` : ''}</div>
              : loaded.map(({ cam, index }) => camRow({
                key: `l-${cam.bSerial}-${cam.bCamera}`,
                rawCam: cam,
                color: `var(--cam-0${index + 1})`,
                idText: String(cam.bCamera).padStart(2, '0'),
                name: cam.sName,
                slotIndex: index,
              }))}
          </div>

          <div className="mcpb-drawer-section-label">Other cameras on this NVR · tap to load</div>
          {/* Capped + internally scrolled so a long NVR (30+ cams) can't push the sections below it
              off-screen — every section label stays visible (David 2026-06-18). */}
          <div className="mcpb-drawer-camlist">
            {available.length === 0
              ? <div className="mcpb-drawer-empty">{ft ? 'No matching cameras' : 'No other cameras on this NVR'}</div>
              : available.map((cam) => camRow({
                key: `a-${cam.bSerial != null ? cam.bSerial : currentSerial}-${cam.bID}`,
                rawCam: cam,
                color: unassignedDot,
                idText: String(cam.bID).padStart(2, '0'),
                name: cam.sName,
                slotIndex: null,
              }))}
          </div>

          {/* Saved layouts + Other systems collapse while searching (matches the mock + inventory §10). */}
          {!ft && (
            <>
              <div className="mcpb-drawer-section-label">Saved layouts · tap to load all cams</div>
              {savedLayouts.length === 0
                ? (
                  <>
                    <div className="mcpb-drawer-empty">No saved layouts yet</div>
                    <div className="mcpb-layouts-empty-hint">Save a camera arrangement to reload it in one tap.</div>
                  </>
                )
                : savedLayouts.map((layout) => (
                  <div className="mcpb-drawer-layout-row" key={layout.id}>
                    {renamingLayoutId === layout.id ? (
                      <input
                        className="mcpb-drawer-layout-row__rename"
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        defaultValue={layout.name}
                        aria-label={`Rename ${layout.name}`}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitLayoutRename(layout.id, e.target.value);
                          else if (e.key === 'Escape') setRenamingLayoutId(null);
                        }}
                        onBlur={(e) => commitLayoutRename(layout.id, e.target.value)}
                      />
                    ) : (
                      <button
                        type="button"
                        className="mcpb-drawer-layout-row__body"
                        onClick={() => onLoadLayout && onLoadLayout(layout)}
                      >
                        <span className="mcpb-drawer-layout-row__name">{layout.name}</span>
                        <span className="mcpb-drawer-layout-row__meta">{(layout.slots || []).length} cams</span>
                      </button>
                    )}
                    <ActionsMenu
                      triggerClassName="mcpb-drawer-layout-row__menu"
                      triggerAriaLabel={`Manage ${layout.name}`}
                      align="right"
                      items={buildLayoutMenuItems({
                        name: layout.name,
                        onRename: () => setRenamingLayoutId(layout.id),
                        onReplace: () => onReplaceLayout && onReplaceLayout(layout.id),
                        onDelete: () => onDeleteLayout && onDeleteLayout(layout.id),
                      })}
                    />
                  </div>
                ))}
              {/* Save the current grid as a layout (inline name entry; disabled at the cap). */}
              {onSaveLayout && (savingName == null ? (
                <button
                  type="button"
                  className="mcpb-drawer-layout-save"
                  disabled={atLayoutCap}
                  onClick={() => setSavingName('')}
                >
                  {atLayoutCap ? '+ Save current (max reached)' : '+ Save current as layout…'}
                </button>
              ) : (
                <div className="mcpb-drawer-layout-saverow">
                  {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                  <input
                    className="mcpb-drawer-layout-saveinput"
                    type="text"
                    autoFocus
                    placeholder="Layout name"
                    value={savingName}
                    onChange={(e) => setSavingName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && savingName.trim()) { onSaveLayout(savingName.trim()); setSavingName(null); } }}
                  />
                  <button
                    type="button"
                    className="mcpb-drawer-layout-savego"
                    disabled={!savingName.trim()}
                    onClick={() => { onSaveLayout(savingName.trim()); setSavingName(null); }}
                  >
                    Save
                  </button>
                  <button type="button" className="mcpb-drawer-layout-savex" aria-label="Cancel save" onClick={() => setSavingName(null)}>
                    &#10005;
                  </button>
                </div>
              ))}

              {isEviewSession && (
                <>
                  <div className="mcpb-drawer-section-label">Other systems · tap to load cameras</div>
                  {otherSystems.length === 0
                    ? <div className="mcpb-drawer-empty">No other systems</div>
                    : otherSystems.map((sys) => {
                      const expanded = !!expandedSystems[sys.serial];
                      return (
                        <div className="mcpb-drawer-system-group" key={`sys-${sys.serial}`}>
                          <button
                            type="button"
                            className={`mcpb-drawer-system-row ${expanded ? 'mcpb-drawer-system-row--open' : ''}`}
                            onClick={() => toggleSystem(sys.serial)}
                          >
                            <span>{sys.name} ({sys.serial})</span>
                            <span className="mcpb-drawer-system-row__count">{expanded ? '▾' : '▸'} {sys.cameras.length} cams</span>
                          </button>
                          {expanded && sys.cameras.map((cam) => camRow({
                            key: `s-${cam.bSerial}-${cam.bID}`,
                            rawCam: cam,
                            color: unassignedDot,
                            idText: String(cam.bID).padStart(2, '0'),
                            name: cam.sName,
                            slotIndex: null,
                          }))}
                        </div>
                      );
                    })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  const { cameras, allCameras, authServers, bSerial } = state.server;
  const { slots } = state.mcplayback;
  return {
    cameras,
    allCameras,
    authServers,
    currentSerial: bSerial,
    slots,
    isEviewSession: isMultiServerSession(state),
  };
};

export default connect(mapStateToProps)(CameraDrawer);
